import {
  computeWeeklyTargets,
  mapCatalogToCanonical,
  weeklyTargetActualStats,
  weeklyTargetDegradedMax,
  type AthleteTargets,
  type BodyMapValue,
  type CatalogMuscleSlug,
  type Exercise,
  type MuscleSlug,
  type Program,
  type TargetActualStats,
  type TargetZone,
  type TimedSessionResult,
  type WeeklyTargetRow,
} from "@jackedlogs/shared";

import { zoneFillColor } from "../primitives/TargetBar";
import type { UiTheme } from "../theme";

// PG-1 — the Progress "muscle coverage" view-model. Pure: composes the SHARED
// weekly scorer (computeWeeklyTargets, the same one Home + the in-workout strip
// use) with the SHARED per-muscle 4-week reference (weeklyTargetActualStats),
// then re-sorts the rows ATTENTION-FIRST (under-target on top) for the Progress
// hero. No scoring lives here — only Progress's presentation ordering + the
// bodymap-zone paint. Muscle-axis only: coverage is about muscles, so exercise-
// axis targets are dropped (they belong to the Strength section, and the bodymap
// figure is muscle-keyed).

// Fixed 7-day window: targets are inherently WEEKLY goals, so coverage is always
// "this week vs your weekly target" regardless of the Progress period selector
// (which drives the trend sections instead). The 4-week sparkline carries the
// longer context. High row cap = the full per-muscle census (Progress isn't the
// compact Home glance).
const CENSUS_MAX_ROWS = 40;

export type CoverageRow = {
  row: WeeklyTargetRow;
  // The per-muscle 4-week reference: { lastWeek, avgPerWeek, weeks[4], hasData }.
  stats: TargetActualStats;
};

export type MuscleCoverage = {
  mode: "scored" | "degraded";
  blockName: string | null;
  // Attention-first: no-coverage + under, then over, then in-band, then the
  // untargeted trained muscles (busiest first).
  rows: CoverageRow[];
  onTargetCount: number;
  targetCount: number;
  // Rows needing attention (no-coverage or under) — drives the lead sentence
  // and how many rows show before the "see all" fold.
  attentionCount: number;
  // Scale for the untargeted (neutral) bars so they stay comparable.
  degradedMax: number;
};

// Lower = more attention. no-coverage (targeted, program never trains it) and
// under both sit on top; the untargeted trained muscles sink to the bottom.
const attentionRank = (r: WeeklyTargetRow): number => {
  if (r.noCoverage) return 0;
  if (r.zone === "under") return 1;
  if (r.zone === "over") return 2;
  if (r.zone === "in-band") return 3;
  return 4; // untargeted / degraded (no zone)
};

/**
 * Build the Progress muscle-coverage view-model, or null when there's nothing
 * to show (mirrors computeWeeklyTargets' null). Fixed 7-day scoring.
 */
export const buildMuscleCoverage = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  program: Program | null,
  prescription: Record<string, number> | null,
  nowMs: number,
  // PG-2 — the athlete's week-start day (0=Sun..6=Sat). Omitted → Monday, via
  // the downstream defaults. Both the scorer and the 4-week stats read it so the
  // hero + trend agree on the same calendar-week boundary.
  weekStartDay?: number,
  // ME-3 — the athlete's own weekly targets (AthleteTargets). The target bands
  // in the census come from these now (the program stays for the prescription
  // coverage). Absent/null → no target bands (the census degrades to neutral
  // rows); the program's embedded targets are not read.
  athleteTargets: AthleteTargets | null = null,
): MuscleCoverage | null => {
  const data = computeWeeklyTargets(
    sessions,
    catalog,
    program,
    nowMs,
    prescription,
    { maxRows: CENSUS_MAX_ROWS, weekStartDay },
    athleteTargets,
  );
  if (!data) return null;

  // Muscle-axis only — the coverage figure + labels are muscle-keyed.
  const muscleRows = data.rows.filter((r) => r.axis === "muscle");
  if (muscleRows.length === 0) return null;

  const rows: CoverageRow[] = muscleRows
    .map((row) => ({
      row,
      stats: weeklyTargetActualStats(
        sessions,
        catalog,
        { axis: row.axis, key: row.key, metric: row.metric },
        nowMs,
        weekStartDay,
      ),
    }))
    .sort((a, b) => {
      const ra = attentionRank(a.row);
      const rb = attentionRank(b.row);
      if (ra !== rb) return ra - rb;
      // Within a zone: biggest shortfall to target first; untargeted busiest
      // first (no goal to fall short of).
      if (a.row.target == null && b.row.target == null) {
        return b.row.actual - a.row.actual;
      }
      const sa = a.row.target != null ? a.row.target - a.row.actual : 0;
      const sb = b.row.target != null ? b.row.target - b.row.actual : 0;
      return sb - sa;
    });

  // Same scale the Home card, the coach panel and the in-workout strip use —
  // resolved from the one shared definition rather than re-derived here. The
  // rows are wrapped (row + stats), so unwrap before handing them over; the
  // seed-1 reduce this replaces was the identical formula on the wrapper.
  const degradedMax = weeklyTargetDegradedMax(rows.map((r) => r.row));

  return {
    mode: data.mode,
    blockName: data.blockName,
    rows,
    onTargetCount: data.onTargetCount,
    targetCount: data.targetCount,
    attentionCount: rows.filter(
      (r) => r.row.noCoverage || r.row.zone === "under",
    ).length,
    degradedMax,
  };
};

// Worst-zone-wins when two catalog muscles bridge onto the same bodymap region
// (a region reads as "needs attention" if any contributing muscle does).
const ZONE_PRIORITY: Record<TargetZone, number> = {
  under: 3,
  over: 2,
  "in-band": 1,
};

// Paint the dual-figure bodymap with CATEGORICAL zone colours (under / over /
// in-band), not a volume magnitude ramp — the coverage figure answers "which
// muscles are off their weekly target", and the colour must agree with each
// row's <TargetBar> fill (both read zoneFillColor). Omits intensity so the zone
// colour renders true (same as the pain picker), instead of rnbh's darkening
// blend. Untargeted / unscored muscles are left unpainted (no goal to score).
export const muscleZoneBodyMapValue = (
  rows: ReadonlyArray<WeeklyTargetRow>,
  theme: UiTheme,
): BodyMapValue => {
  const best: Partial<Record<MuscleSlug, TargetZone>> = {};
  for (const r of rows) {
    // Skip no-coverage (AP-9): the row carries zone "under" but its meaning is
    // "the program never trains this" — painting it as under would conflate two
    // states the row list keeps distinct (∅ vs •). Left unpainted on the figure;
    // the ∅ glyph+word on the row is the sole carrier of that state.
    if (r.axis !== "muscle" || r.zone == null || r.noCoverage) continue;
    for (const bm of mapCatalogToCanonical(r.key as CatalogMuscleSlug)) {
      const cur = best[bm];
      if (cur == null || ZONE_PRIORITY[r.zone] > ZONE_PRIORITY[cur]) {
        best[bm] = r.zone;
      }
    }
  }
  const out: BodyMapValue = {};
  for (const [bm, zone] of Object.entries(best)) {
    if (!zone) continue;
    out[bm as MuscleSlug] = { colour: zoneFillColor(theme, zone) };
  }
  return out;
};
